import { ObservableInput, OperatorFunction, catchError, from, map, of, pipe, scan, startWith } from 'rxjs';

export type ObservableState<T> =
  | { readonly state: 'init'; readonly data?: undefined }
  | { readonly state: 'ok'; readonly data: T }
  | { readonly state: 'error'; readonly data?: undefined; readonly error: unknown };
const loadingState = {
  state: 'init',
} as const;

export const observeState = <T>(): OperatorFunction<T, ObservableState<T>> =>
  pipe(
    map(
      (data) =>
        ({
          state: 'ok',
          data,
        }) as const,
    ),
    startWith(loadingState),
    catchError((error) =>
      of({
        state: 'error',
        error,
      } as const),
    ),
  );

export const observeStateFrom = <O extends ObservableInput<unknown>>(input: O) => from(input).pipe(observeState());

export type PreservingObservableState<T> = ObservableState<T> | { readonly state: 'old'; readonly data: T };

export const preserveTillNext = <T>(): OperatorFunction<ObservableState<T>, PreservingObservableState<T>> =>
  scan((acc, value) => {
    if (value.state !== 'init') {
      return value;
    }

    if (acc.state === 'ok' || acc.state === 'old') {
      return {
        state: 'old',
        data: acc.data,
      };
    }

    return acc;
  });
